"use client";

import { useEffect, useRef } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { useRealtimeKitSelector } from "@cloudflare/realtimekit-react";

import useUserStore from "@/features/auth/store/useUserStore";

interface VideoParticipantProps {
  participant: any;
  compact?: boolean;
}

interface AudioParticipantProps {
  participant: any;
}

// VideoPlayer component for rendering participant video tracks
function VideoPlayer({
  track,
  isLocal = false,
}: {
  track: MediaStreamTrack | null;
  isLocal?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current && track) {
      // Create a new stream and add the track to it
      const mediaStream = new MediaStream([track]);

      videoRef.current.srcObject = mediaStream;
    } else if (videoRef.current) {
      // Clear the stream if the track is removed
      videoRef.current.srcObject = null;
    }
  }, [track]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted // Mute the video element; audio is handled globally by the SDK
      playsInline
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        // Mirror local video for a natural feel
        transform: isLocal ? "scaleX(-1)" : "none",
      }}
    />
  );
}

function AudioPlayer({
  isLocal = false,
  track,
}: {
  track: MediaStreamTrack | null;
  isLocal?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (audioRef.current && track) {
      // Create a new stream and add the track to it
      const mediaStream = new MediaStream([track]);

      audioRef.current.srcObject = mediaStream;
    } else if (audioRef.current) {
      // Clear the stream if the track is removed
      audioRef.current.srcObject = null;
    }
  }, [track]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <audio ref={audioRef} autoPlay muted={isLocal} /> //don't ever change it to have tracks
  );
}

// Audio component - always renders for all remote participants
export function AudioParticipant({ participant }: AudioParticipantProps) {
  const { user } = useUserStore();

  if (!participant) {
    return null;
  }

  const isLocal = participant?.customParticipantId === user?.id;

  // Only render audio for remote participants
  if (isLocal) {
    return null;
  }

  const audioStream = participant.audioTrack;
  const isAudioOn = participant.audioEnabled;

  return <>{audioStream && isAudioOn && <AudioPlayer isLocal={false} track={audioStream} />}</>;
}

// Video component - renders participant video without audio
export function VideoParticipant({ participant, compact = false }: VideoParticipantProps) {
  const { t } = useTranslation(["common", "classroom"]);
  const { user } = useUserStore();
  const self = useRealtimeKitSelector((m) => m.self);

  if (!participant) {
    return null;
  }

  // Extract participant properties
  const displayName = participant.name || t("common:participant");
  const isLocal = participant?.customParticipantId === user?.id;

  // Get participant tracks
  const webcamStream = participant.videoTrack;
  const isVideoOn = participant.videoEnabled && webcamStream;
  const isAudioOn = !isLocal ? participant.audioEnabled : self?.audioEnabled;

  return (
    <motion.div
      animate={{ opacity: 1, scale: 1 }}
      className={`relative h-full w-full overflow-hidden rounded-lg transition-all duration-300`}
      initial={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.3 }}
    >
      {isVideoOn && webcamStream ? (
        <div className="bg-side-accent flex h-full w-full items-center justify-center">
          <VideoPlayer isLocal={isLocal} track={webcamStream} />
        </div>
      ) : (
        <div className="bg-side-accent flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center justify-center space-y-2">
            {participant.picture ? (
              <div
                className={`${compact ? "h-12 w-12" : "h-16 w-16 max-[768px]:h-14 max-[768px]:w-14"} flex-shrink-0 overflow-hidden rounded-full`}
              >
                <img
                  alt={displayName || t("common:participant")}
                  className="h-full w-full object-cover"
                  src={participant.picture || "/placeholder.svg"}
                />
              </div>
            ) : (
              <div
                className={`bg-background text-accent-foreground flex ${
                  compact
                    ? "h-12 w-12 text-lg"
                    : "h-16 w-16 text-2xl max-[768px]:h-14 max-[768px]:w-14 max-[768px]:text-xl"
                } flex-shrink-0 items-center justify-center rounded-full font-medium`}
              >
                {displayName?.charAt(0)?.toUpperCase() || "?"}
              </div>
            )}
            <p
              className={`text-accent-foreground ${
                compact ? "text-xs" : "text-sm max-[768px]:text-xs"
              } max-w-full truncate px-1 text-center font-medium`}
            >
              {displayName || t("common:participant")}
            </p>
          </div>
        </div>
      )}

      {/* Name overlay for video */}
      <div className="bg-background/60 text-accent-foreground absolute bottom-0 left-0 m-2 rounded px-2 py-1 text-sm max-[768px]:text-xs">
        {displayName || t("common:participant")}
      </div>

      {/* Status indicators */}
      <div className="absolute right-2 bottom-2 flex space-x-1">
        {!isAudioOn && (
          <div className="bg-background/80 text-accent-foreground flex h-6 w-6 items-center justify-center rounded-full max-[768px]:h-5 max-[768px]:w-5">
            <Icon height="12" icon="lucide:mic-off" width="12" />
          </div>
        )}
        {!isVideoOn && (
          <div className="bg-background/80 text-accent-foreground flex h-6 w-6 items-center justify-center rounded-full max-[768px]:h-5 max-[768px]:w-5">
            <Icon height="12" icon="lucide:video-off" width="12" />
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Legacy component for backward compatibility - now just wraps VideoParticipant
export default function MeetingParticipant({
  audioOnly = false,
  compact = false,
  participant,
}: {
  participant: any;
  compact?: boolean;
  audioOnly?: boolean;
}) {
  if (audioOnly) {
    return <AudioParticipant participant={participant} />;
  }

  return <VideoParticipant compact={compact} participant={participant} />;
}
